import React, { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import API from "../../Common/API"
import DatatableLoader from "../../Components/Loaders/DataTableLoader"
import { datatableTheme } from "../../Common/dataTableTheme"
import DataTable, { createTheme } from "react-data-table-component"
import InlineTextField from "../../Components/InLineTextField"
import { useHistory } from "react-router-dom"
import { FaPlus } from "react-icons/fa6"
import { useDispatch } from "react-redux"
import { setAlert } from "../../Store/Theme/actions"
import { FiEdit3, FiTrash2 } from "react-icons/fi"
import { HiOutlineExclamationTriangle, HiOutlineXMark, HiOutlineTrash, HiOutlineNoSymbol } from "react-icons/hi2"
import TableActionButton from "../../Components/TableActionButton"
import Dialog from "../../Components/Dialog"
import CreatePlan from "./CreatePlan"

createTheme("defaultLight", datatableTheme.default)
createTheme("defaultDark", datatableTheme.dark)

const Plans = () => {
    const [planList, setPlanList] = useState([])
    const [loading, setLoading] = useState(true)
    const [filter, setFilter] = useState("")
    const history = useHistory()
    const dispatch = useDispatch()
    const [count, setCount] = useState(0)
    const [showCreatePlan, setShowCreatePlan] = useState(false)
    const [statusUpdatingId, setStatusUpdatingId] = useState(null)
    const [deleteTarget, setDeleteTarget] = useState(null)   // plan row to delete
    const [deleteLoading, setDeleteLoading] = useState(false)

    useEffect(() => {
        setLoading(true)
        API.get("/plans/getAllPlans").then((planRes) => {
            if (planRes.status === 200) {
                setPlanList(planRes.data.data)
                setLoading(false)
            }
        }).catch(() => setLoading(false))
    }, [count])

    const togglePlanStatus = async (row) => {
        if (statusUpdatingId === row._id) return

        try {
            setStatusUpdatingId(row._id)
            const endpoint = row.viewAble ? "/plans/deactivate" : "/plans/activate"
            const response = await API.post(endpoint, row)
            if (response.status === 200) {
                dispatch(setAlert({
                    title: "Success",
                    subtitle: row.viewAble ? "Plan hidden successfully" : "Plan published successfully",
                    active: true,
                    type: "success",
                }))
                setCount((prev) => prev + 1)
            }
        } catch (error) {
            dispatch(setAlert({ title: "Error", subtitle: "Unable to update plan status right now", active: true, type: "error" }))
        } finally {
            setStatusUpdatingId(null)
        }
    }

    const handleDelete = async () => {
        if (!deleteTarget || deleteLoading) return
        setDeleteLoading(true)
        try {
            const res = await API.post("/plans/deletePlan", { _id: deleteTarget._id })
            if (res.status === 200) {
                const action = res.data?.action
                dispatch(setAlert({
                    title: action === "deleted" ? "Plan Deleted" : "Plan Disabled",
                    subtitle: action === "deleted"
                        ? `"${deleteTarget.planName}" has been permanently deleted.`
                        : `"${deleteTarget.planName}" has existing subscribers — it has been disabled and hidden instead.`,
                    active: true,
                    type: action === "deleted" ? "success" : "warning",
                }))
                setDeleteTarget(null)
                setCount((prev) => prev + 1)
            }
        } catch (err) {
            dispatch(setAlert({ title: "Error", subtitle: "Could not delete the plan. Please try again.", active: true, type: "error" }))
        } finally {
            setDeleteLoading(false)
        }
    }

    const columns = useMemo(() => [
        { name: "S.No", selector: (row, i) => i + 1, initialActive: true, sortable: true, width: "100px" },
        { name: "Plan Name", selector: (row) => row.planName, initialActive: true, sortable: true },
        { name: "Plan Rate", selector: (row) => row.planRate, sortable: true },
        { name: "Plan Validity", selector: (row) => row.planValidity, sortable: true },
        { name: "Download Limit", selector: (row) => row.downloadLimit, sortable: true },
        { name: "Benefits", selector: (row) => (row.planBenefits || []).length, sortable: true },
        {
            name: "Status",
            minWidth: "180px",
            selector: (row) => row.viewAble,
            cell: (row) => (
                <label className={`inline-flex min-w-[138px] cursor-pointer items-center justify-between rounded-full border px-3 py-2 text-sm font-bold transition ${row.viewAble ? "border-green-200 bg-green-50 text-green-700" : "border-slate-200 bg-slate-100 text-slate-600"}`}>
                    <span>{row.viewAble ? "Published" : "Hidden"}</span>
                    <span className="relative ml-3 inline-flex items-center">
                        <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={row.viewAble}
                            disabled={statusUpdatingId === row._id}
                            onChange={() => togglePlanStatus(row)}
                        />
                        <span className={`block h-6 w-11 rounded-full transition ${row.viewAble ? "bg-green-500" : "bg-slate-300"}`} />
                        <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white shadow-sm transition ${row.viewAble ? "translate-x-5" : "translate-x-0"}`} />
                    </span>
                </label>
            ),
            sortable: true,
        },
        {
            name: "Action",
            minWidth: "140px",
            selector: (row) => (
                <motion.div className="relative flex w-full flex-row justify-start gap-2">
                    <TableActionButton title="Edit plan" tone="primary" onClick={() => history.push(`/editPlan/${row._id}`)}>
                        <FiEdit3 />
                    </TableActionButton>
                    <TableActionButton title="Delete plan" tone="danger" onClick={() => setDeleteTarget(row)}>
                        <FiTrash2 />
                    </TableActionButton>
                </motion.div>
            ),
            initialActive: true,
            sortable: true
        }
    ], [history, statusUpdatingId, togglePlanStatus]) // eslint-disable-line react-hooks/exhaustive-deps

    const filteredcasedetails = useMemo(() => {
        const filterSplit = filter.toLowerCase().split(" ")
        return planList.filter((c) =>
            filterSplit.every((i) =>
                Object.keys(c).some((j) => c[j]?.toString().toLowerCase().includes(i))
            )
        )
    }, [planList, filter])

    const SubHeader = useMemo(() => (
        <div className="table-toolbar">
            <span className="status-pill status-pill--success">{filteredcasedetails.length} plans</span>
            <div className="toolbar-actions">
                <InlineTextField label={"Plans"} placeholder={"Search plan name, rate, or validity"} value={filter} setValue={setFilter} />
                <motion.button type="button" className="flex h-[46px] w-[46px] items-center justify-center rounded-2xl bg-tColor text-white shadow-soft transition hover:bg-primeColor" onClick={() => setShowCreatePlan(true)}><FaPlus /></motion.button>
            </div>
        </div>
    ), [filter, filteredcasedetails.length])

    return (
        <motion.div className="space-y-5">
            {loading ? (
                <DatatableLoader />
            ) : (
                <motion.div className="datatable-shell panel-card h-full w-full overflow-hidden">
                    <DataTable
                        title="Plan List"
                        theme="defaultLight"
                        columns={columns}
                        data={filteredcasedetails}
                        pagination
                        highlightOnHover
                        selectableRowsHighlight
                        persistTableHead
                        subHeader
                        subHeaderComponent={SubHeader}
                        noDataComponent={"There are no records to display"}
                    />
                </motion.div>
            )}
            <Dialog injectClass="max-w-5xl" showDialog={showCreatePlan} title={"Create Plan"} onClose={() => setShowCreatePlan(false)}>
                <CreatePlan
                    embedded
                    onClose={() => setShowCreatePlan(false)}
                    onCreated={() => {
                        setShowCreatePlan(false)
                        setCount((prev) => prev + 1)
                    }}
                />
            </Dialog>

            {/* ── Delete Confirmation Modal ── */}
            <AnimatePresence>
                {deleteTarget && (
                    <motion.div
                        className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => { if (!deleteLoading) setDeleteTarget(null) }}
                    >
                        <motion.div
                            className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
                            initial={{ scale: 0.93, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 20 }}
                            transition={{ type: "spring", damping: 28, stiffness: 280 }}
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between border-b border-red-100 bg-red-50 px-5 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-100">
                                        <HiOutlineExclamationTriangle className="h-5 w-5 text-red-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-[0.12em] text-red-500">Confirm Action</p>
                                        <h3 className="text-sm font-bold text-red-800">Delete Plan</h3>
                                    </div>
                                </div>
                                {!deleteLoading && (
                                    <button type="button" onClick={() => setDeleteTarget(null)}
                                        className="flex h-8 w-8 items-center justify-center rounded-full border border-red-200 text-red-400 hover:text-red-600 transition">
                                        <HiOutlineXMark className="h-4 w-4" />
                                    </button>
                                )}
                            </div>

                            {/* Body */}
                            <div className="px-5 py-5 space-y-4">
                                <p className="text-sm text-slate-700 leading-relaxed">
                                    You are about to delete{" "}
                                    <span className="font-bold text-slate-900">"{deleteTarget.planName}"</span>
                                    {" "}(₹{(deleteTarget.planRate || 0).toLocaleString("en-IN")} · {deleteTarget.planValidity || deleteTarget.validityInDays || "—"} days).
                                </p>

                                {/* Smart-delete info banner */}
                                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-1.5">
                                    <div className="flex items-start gap-2">
                                        <HiOutlineTrash className="h-4 w-4 shrink-0 text-amber-700 mt-0.5" />
                                        <p className="text-xs font-semibold text-amber-800">
                                            If no user has paid for this plan — it will be <span className="underline">permanently deleted</span>.
                                        </p>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <HiOutlineNoSymbol className="h-4 w-4 shrink-0 text-amber-700 mt-0.5" />
                                        <p className="text-xs font-semibold text-amber-800">
                                            If any user has paid for this plan — it will be <span className="underline">disabled and hidden</span> instead, so existing subscribers are not affected.
                                        </p>
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex gap-3 pt-1">
                                    <button type="button"
                                        onClick={() => setDeleteTarget(null)}
                                        disabled={deleteLoading}
                                        className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition">
                                        Cancel
                                    </button>
                                    <button type="button"
                                        onClick={handleDelete}
                                        disabled={deleteLoading}
                                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60 transition">
                                        {deleteLoading
                                            ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />Processing…</>
                                            : <><HiOutlineTrash className="h-4 w-4" />Yes, Delete</>
                                        }
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}

export default Plans
